import { useEffect, useMemo, useRef, useState } from "react";
import { createCatalogItem, deleteCatalogItem, getCatalogItems, swapCatalogItemOrder, updateCatalogItem, getSites } from "../api";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import ImportExportOverlay from "../components/management/ImportExportOverlay";
import VestaSelect from "../components/common/VestaSelect";
import { OverlayFieldLabel } from "../components/common/OverlayValidation";
import DefaultNoteTextarea from "../components/common/DefaultNoteTextarea";
import { getUserPageLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../utils/permissions";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/services.css";

const SERVICE_ENDPOINT = "hotel-services";
const EMPTY_SERVICE = { description: "", rate: 0, defaultQuantity: 1, vatRate: 0, defaultChargeType: "FORFAIT", notes: "", siteIds: [], order: 0, active: true };
const EMPTY_FILTERS = { description: [], rate: [], defaultQuantity: [], taxableTotal: [], vatRate: [], taxAmount: [], grossTotal: [], defaultChargeType: [] };
const CHARGE_TYPE_LABELS = { FORFAIT: "Forfait", DAILY: "Giornaliero", HOURLY: "Orario", ACCUMULO: "Accumulo" };
const SERVICE_IMPORT_EXPORT_FIELDS = [
  { key: "description", label: "Descrizione", required: true },
  { key: "rate", label: "Prezzo", type: "number", aliases: ["Tariffa", "Importo"] },
  { key: "defaultQuantity", label: "Qta", type: "number", aliases: ["QTA Default", "Quantità Default", "Quantita Default", "Quantita"] },
  { key: "vatRate", label: "% IVA", type: "number", aliases: ["Aliquota IVA", "IVA"] },
  { key: "defaultChargeType", label: "Tipo addebito", aliases: ["Addebito"], parse: parseChargeType, exportValue: (row) => CHARGE_TYPE_LABELS[row.defaultChargeType] || row.defaultChargeType || "Forfait" },
  { key: "notes", label: "Note", aliases: ["Nota", "Note Servizio"] },
];

function displayValue(value) {
  const text = String(value || "").trim();
  return text || "-";
}

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function serviceAmounts(item = {}) {
  const price = Number(item.rate || 0);
  const quantity = Number(item.defaultQuantity || 0);
  const vatRate = Math.min(100, Math.max(0, Number(item.vatRate || 0)));
  const taxableTotal = price * quantity;
  const taxAmount = taxableTotal * vatRate / 100;
  return { taxableTotal, vatRate, taxAmount, grossTotal: taxableTotal + taxAmount };
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

function parseChargeType(value) {
  const token = String(value || "").trim().toUpperCase();
  if (["DAILY", "GIORNALIERO", "GIORNALIERA", "GIORNO", "GIORNI"].includes(token)) return "DAILY";
  if (["HOURLY", "ORARIO", "ORARIA", "ORA", "ORE"].includes(token)) return "HOURLY";
  if (["ACCUMULO", "ACCUMULATO", "ACCUMULATA"].includes(token)) return "ACCUMULO";
  return "FORFAIT";
}

function chargeTypeLabel(value) {
  return CHARGE_TYPE_LABELS[String(value || "").toUpperCase()] || "Forfait";
}


function normalizeOrderValue(value) {
  const raw = Math.round(Number(value || 0) / 10) * 10;
  return Math.max(10, Number.isFinite(raw) ? raw : 10);
}

function OrderActionField({ value, blocked, onBlocked, onMoveUp, onMoveDown, onCommit }) {
  const [localValue, setLocalValue] = useState(normalizeOrderValue(value));

  useEffect(() => {
    setLocalValue(normalizeOrderValue(value));
  }, [value]);

  function guard(action) {
    if (blocked) {
      onBlocked?.();
      return;
    }
    action?.();
  }

  function commit() {
    const normalized = normalizeOrderValue(localValue);
    setLocalValue(normalized);
    guard(() => onCommit(normalized));
  }

  return (
    <div className={`management_order_action_field${blocked ? " is_permission_blocked" : ""}`} aria-label="Ordine" data-disabled-tooltip={UNAUTHORIZED_LABEL}>
      <button type="button" className={`management_order_arrow${blocked ? " is_permission_blocked" : ""}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onMouseDown={(event) => event.preventDefault()} onClick={() => guard(onMoveUp)} aria-label="Sposta ordine su">▲</button>
      <input type="number" min="10" step="10" value={localValue || ""} onChange={(event) => setLocalValue(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} aria-label="Ordine" />
      <button type="button" className={`management_order_arrow${blocked ? " is_permission_blocked" : ""}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onMouseDown={(event) => event.preventDefault()} onClick={() => guard(onMoveDown)} aria-label="Sposta ordine giù">▼</button>
    </div>
  );
}

function OptionsMenu({ open, menuRef, disabled, confirming, canEdit, canDelete, blockedClass, onBlocked, onToggle, onNew, onReset, onImport, onExport, onStartConfirm, onCancelConfirm, onConfirm }) {
  return (
    <div className="management_options_menu" ref={menuRef}>
      <button type="button" className="management_options_button" onClick={onToggle} aria-label="Opzioni servizi" aria-expanded={open}>
        <span></span><span></span><span></span>
      </button>
      {open && (
        <div className="management_options_dropdown">
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onNew : onBlocked}>Nuovo Servizio</button>
          <button type="button" className="management_options_item" onClick={onReset}>Azzera ricerca e filtri</button>
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onImport : onBlocked}>Importa Valori</button>
          <button type="button" className="management_options_item" onClick={onExport}>Esporta Valori</button>
          {confirming ? (
            <div className="management_options_confirm_row">
              <button type="button" className="management_options_item is_neutral" onClick={onCancelConfirm}>Annulla</button>
              <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canDelete ? onConfirm : onBlocked}>Conferma</button>
            </div>
          ) : (
            <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={disabled} onClick={canDelete ? onStartConfirm : onBlocked}>Eliminazione Multipla</button>
          )}
        </div>
      )}
    </div>
  );
}

function ServiceOverlay({ draft, sites, error, onChange, onClose, onSave }) {
  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card" role="dialog" aria-modal="true" aria-label="Servizio">
        <div className="overlay_header">
          <h2>{draft._id ? "MODIFICA SERVIZIO" : "NUOVO SERVIZIO"}</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="overlay_body">
          <div className="overlay_section">
            <h3>Dati Servizio</h3>
            <div className="overlay_fields_grid">
              <label className="overlay_field overlay_field_full"><OverlayFieldLabel required>Descrizione</OverlayFieldLabel><input value={draft.description || ""} onChange={(e) => onChange("description", e.target.value)} /></label>
              <label className="overlay_field"><span>Prezzo</span><input type="number" min="0" step="0.01" value={draft.rate ?? 0} onChange={(e) => onChange("rate", e.target.value)} /></label>
              <label className="overlay_field"><span>Qta</span><input type="number" min="0.01" step="0.01" value={draft.defaultQuantity ?? 1} onChange={(e) => onChange("defaultQuantity", e.target.value)} /></label>
              <label className="overlay_field"><span>% IVA</span><input type="number" min="0" max="100" step="0.01" value={draft.vatRate ?? 0} onChange={(e) => onChange("vatRate", e.target.value)} /></label>
              <label className="overlay_field"><span>Tipo addebito</span><VestaSelect value={draft.defaultChargeType || "FORFAIT"} onChange={(event) => onChange("defaultChargeType", event.target.value)}><option value="FORFAIT">Forfait</option><option value="DAILY">Giornaliero</option><option value="HOURLY">Orario</option><option value="ACCUMULO">Accumulo</option></VestaSelect></label>
              <div className="overlay_field overlay_field_full"><span>Sedi abilitate</span>
                <div className="service_sites_options">{sites.map((site) => <label key={site._id} className="service_site_option">
                  <input type="checkbox" checked={(draft.siteIds || []).includes(site._id)} onChange={(event) => onChange('siteIds', event.target.checked ? [...new Set([...(draft.siteIds || []), site._id])] : (draft.siteIds || []).filter((id) => id !== site._id))} />
                  <span>{site.description}</span>
                </label>)}</div>
              </div>
              <label className="overlay_field overlay_field_full"><span>Note Servizio</span><DefaultNoteTextarea value={draft.notes || ""} noteType="Servizio" onChange={(value) => onChange("notes", value)} /></label>
              <div className="overlay_field overlay_field_full service_amount_preview"><span>Totali servizio</span><div><strong>Totale Imponibile {formatMoney(serviceAmounts(draft).taxableTotal)}</strong><strong>Totale Imposta {formatMoney(serviceAmounts(draft).taxAmount)}</strong><strong>Totale Ivato {formatMoney(serviceAmounts(draft).grossTotal)}</strong></div></div>
            </div>
          </div>
        </div>
        <div className="overlay_footer"><div className="overlay_error_message">{error}</div><button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>Salva e Chiudi</button></div>
      </div>
    </div>
  );
}

export default function Services() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(EMPTY_SERVICE);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayError, setOverlayError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);
  const [sortState, setSortState] = useState({ key: "description", direction: "asc" });
  const [importExportMode, setImportExportMode] = useState("");
  const optionsMenuRef = useRef(null);
  const filterPanelRef = useRef(null);
  const user = readStoredUser();
  const serviceLevel = getUserPageLevel(user, "accommodationService");
  const canEditService = serviceLevel >= 2;
  const canDeleteService = serviceLevel >= 3;
  const blockedActionClass = (allowed) => allowed ? "" : " is_permission_blocked";
  const blockAction = () => {};

  async function loadItems() {
    setLoading(true);
    setError("");
    try {
      const [data, siteData] = await Promise.all([getCatalogItems(SERVICE_ENDPOINT, { includeInactive: true }), getSites()]);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSites((siteData?.items || []).filter((site) => site.active !== false));
    } catch {
      setError("Impossibile caricare i servizi");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadItems(); }, []);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setOptionsOpen(false);
        setConfirmBulkDelete(false);
      }
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => { if (selectedIds.length === 0) setConfirmBulkDelete(false); }, [selectedIds]);

  const filterOptions = useMemo(() => ({
    description: makeManagementFilterOptions(items, (item) => item.description),
    rate: makeManagementFilterOptions(items, (item) => formatMoney(item.rate)),
    defaultQuantity: makeManagementFilterOptions(items, (item) => item.defaultQuantity),
    taxableTotal: makeManagementFilterOptions(items, (item) => formatMoney(serviceAmounts(item).taxableTotal)),
    vatRate: makeManagementFilterOptions(items, (item) => formatPercent(serviceAmounts(item).vatRate)),
    taxAmount: makeManagementFilterOptions(items, (item) => formatMoney(serviceAmounts(item).taxAmount)),
    grossTotal: makeManagementFilterOptions(items, (item) => formatMoney(serviceAmounts(item).grossTotal)),
    defaultChargeType: makeManagementFilterOptions(items, (item) => chargeTypeLabel(item.defaultChargeType)),
  }), [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const rateText = formatMoney(item.rate);
      const chargeText = chargeTypeLabel(item.defaultChargeType);
      const amounts = serviceAmounts(item);
      const taxableText = formatMoney(amounts.taxableTotal);
      const vatText = formatPercent(amounts.vatRate);
      const taxText = formatMoney(amounts.taxAmount);
      const grossText = formatMoney(amounts.grossTotal);
      const matchesSearch = matchesManagementSearch([item.description, rateText, item.defaultQuantity, taxableText, vatText, taxText, grossText, chargeText], search);
      if (!matchesSearch) return false;
      return matchesManagementFilter(rateText, columnFilters.rate, "number")
        && matchesManagementFilter(item.defaultQuantity, columnFilters.defaultQuantity, "number")
        && matchesManagementFilter(taxableText, columnFilters.taxableTotal, "number")
        && matchesManagementFilter(vatText, columnFilters.vatRate, "number")
        && matchesManagementFilter(taxText, columnFilters.taxAmount, "number")
        && matchesManagementFilter(grossText, columnFilters.grossTotal, "number")
        && matchesManagementFilter(chargeText, columnFilters.defaultChargeType);
    });
  }, [items, search, columnFilters]);

  const serviceSortGetters = useMemo(() => ({
    description: { getter: (item) => item.description, type: "text" },
    rate: { getter: (item) => item.rate, type: "number" },
    defaultQuantity: { getter: (item) => item.defaultQuantity, type: "number" },
    taxableTotal: { getter: (item) => serviceAmounts(item).taxableTotal, type: "number" },
    vatRate: { getter: (item) => serviceAmounts(item).vatRate, type: "number" },
    taxAmount: { getter: (item) => serviceAmounts(item).taxAmount, type: "number" },
    grossTotal: { getter: (item) => serviceAmounts(item).grossTotal, type: "number" },
    defaultChargeType: { getter: (item) => chargeTypeLabel(item.defaultChargeType), type: "text" },
  }), []);
  const orderedFiltered = useMemo(() => sortManagementRows(filtered, sortState, serviceSortGetters), [filtered, sortState, serviceSortGetters]);

  function openService(item = null) {
    if (!canEditService) { blockAction(); return; }
    setOverlayError("");
    setConfirmDeleteId("");
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
    const enabledIds = Array.isArray(item?.siteIds) ? item.siteIds : (item ? sites.map((site) => site._id) : [sites.find((site) => site._id === user?.defaultSiteId)?._id || sites[0]?._id].filter(Boolean));
    setDraft({ ...EMPTY_SERVICE, ...(item || {}), siteIds: enabledIds, defaultChargeType: parseChargeType(item?.defaultChargeType || "FORFAIT") });
    setShowOverlay(true);
  }

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function buildPayload(source) {
    return {
      ...source,
      description: String(source.description || "").trim(),
      rate: Number(String(source.rate ?? 0).replace(",", ".")) || 0,
      defaultQuantity: Number(String(source.defaultQuantity ?? 1).replace(",", ".")) || 1,
      vatRate: Math.min(100, Math.max(0, Number(String(source.vatRate ?? 0).replace(",", ".")) || 0)),
      defaultChargeType: parseChargeType(source.defaultChargeType),
      notes: String(source.notes || "").trim(),
      order: normalizeOrderValue(source.order),
      active: source.active !== false,
    };
  }

  async function saveService() {
    if (!canEditService) { setOverlayError(UNAUTHORIZED_LABEL); return; }
    setOverlayError("");
    const payload = buildPayload(draft);
    if (!payload.description || !payload.siteIds?.length) return setOverlayError("Seleziona almeno una sede per il servizio");
    try {
      const data = payload._id ? await updateCatalogItem(SERVICE_ENDPOINT, payload._id, payload) : await createCatalogItem(SERVICE_ENDPOINT, payload);
      const saved = data?.item;
      if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
      setShowOverlay(false);
    } catch (err) {
      setOverlayError(err?.response?.data?.error || "Dati Errati");
    }
  }

  function openImportExport(mode) {
    if (mode === "import" && !canEditService) { blockAction(); return; }
    setImportExportMode(mode);
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
  }

  async function importServices(records) {
    if (!canEditService) throw new Error(UNAUTHORIZED_LABEL);
    let imported = 0;
    for (const record of records) {
      try {
        const data = await createCatalogItem(SERVICE_ENDPOINT, buildPayload({ ...EMPTY_SERVICE, ...record, siteIds: [user?.defaultSiteId || sites[0]?._id].filter(Boolean) }));
        const saved = data?.item;
        if (saved) setItems((current) => current.some((item) => item._id === saved._id) ? current.map((item) => item._id === saved._id ? saved : item) : [...current, saved]);
        imported += 1;
      } catch (err) {
        throw new Error(`Riga ${imported + 2}: ${err?.response?.data?.error || "Dati Errati"}`);
      }
    }
    return { imported };
  }

  async function toggleActive(item) {
    if (!canEditService) { blockAction(); return; }
    try {
      const data = await updateCatalogItem(SERVICE_ENDPOINT, item._id, buildPayload({ ...item, active: item.active === false }));
      const saved = data?.item;
      if (saved) setItems((current) => current.map((row) => row._id === saved._id ? saved : row));
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile aggiornare lo stato servizio");
    }
  }

  async function persistOrderChange(item, nextOrder, swapItem = null) {
    if (!canEditService) { blockAction(); return; }
    const currentOrder = normalizeOrderValue(item.order);
    if (!swapItem && currentOrder === nextOrder) return;
    const previousItems = items;
    setError("");
    setItems((current) => current.map((row) => {
      if (String(row._id) === String(item._id)) return { ...row, order: nextOrder };
      if (swapItem && String(row._id) === String(swapItem._id)) return { ...row, order: currentOrder };
      return row;
    }));
    try {
      const data = await swapCatalogItemOrder(SERVICE_ENDPOINT, { sourceId: item._id, targetId: swapItem?._id || null, order: nextOrder });
      const savedItems = Array.isArray(data?.items) ? data.items : [];
      if (savedItems.length) setItems((current) => current.map((row) => savedItems.find((saved) => String(saved._id) === String(row._id)) || row));
    } catch (err) {
      setItems(previousItems);
      setError(err?.response?.data?.error || "Impossibile aggiornare l'ordine servizio");
      await loadItems();
    }
  }

  async function moveRowOrder(item, direction, visibleRows) {
    const rows = Array.isArray(visibleRows) ? visibleRows : [];
    const index = rows.findIndex((row) => String(row._id) === String(item._id));
    const swapItem = rows[index + direction];
    if (!swapItem) return;
    await persistOrderChange(item, normalizeOrderValue(swapItem.order), swapItem);
  }

  async function commitRowOrder(item, value) {
    const nextOrder = normalizeOrderValue(value);
    const swapItem = items.find((row) => String(row._id) !== String(item._id) && normalizeOrderValue(row.order) === nextOrder);
    await persistOrderChange(item, nextOrder, swapItem);
  }

  async function removeService(item) {
    if (!canDeleteService) { blockAction(); setConfirmDeleteId(""); return; }
    try {
      await deleteCatalogItem(SERVICE_ENDPOINT, item._id);
      setItems((current) => current.filter((row) => row._id !== item._id));
      setSelectedIds((current) => current.filter((id) => String(id) !== String(item._id)));
      setConfirmDeleteId("");
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare il servizio");
    }
  }

  async function removeSelected() {
    if (selectedIds.length === 0) return;
    if (!canDeleteService) { blockAction(); setConfirmBulkDelete(false); return; }
    try {
      await Promise.all(selectedIds.map((id) => deleteCatalogItem(SERVICE_ENDPOINT, id)));
      setItems((current) => current.filter((row) => !selectedIds.some((id) => String(id) === String(row._id))));
      setSelectedIds([]);
      setConfirmBulkDelete(false);
      setOptionsOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare i servizi selezionati");
    }
  }

  function toggleFilter(columnKey) { setActiveFilter((current) => (current === columnKey ? "" : columnKey)); }
  function updateCheckFilter(columnKey, value, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      return { ...current, [columnKey]: checked ? [...values, value] : values.filter((item) => item !== value) };
    });
  }
  function clearColumnFilter(columnKey) { setColumnFilters((current) => ({ ...current, [columnKey]: [] })); }
  function updateRangeFilter(columnKey, value) { setColumnFilters((current) => ({ ...current, [columnKey]: value })); }
  function toggleSort(columnKey) { setSortState((current) => ({ key: columnKey, direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc" })); }
  function resetSearchAndFilters() { setSearch(""); setColumnFilters(EMPTY_FILTERS); setSortState({ key: "description", direction: "asc" }); setActiveFilter(""); setOptionsOpen(false); setConfirmBulkDelete(false); }
  function toggleSelected(id, checked) { setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((item) => String(item) !== String(id))); }
  function toggleAllFiltered(checked) {
    const ids = orderedFiltered.map((item) => item._id);
    setSelectedIds((current) => checked ? [...new Set([...current, ...ids])] : current.filter((id) => !ids.some((itemId) => String(itemId) === String(id))));
  }

  const ids = orderedFiltered.map((item) => item._id);
  const allFilteredSelected = ids.length > 0 && ids.every((id) => selectedIds.some((item) => String(item) === String(id)));
  const someFilteredSelected = ids.some((id) => selectedIds.some((item) => String(item) === String(id)));
  const filterProps = { activeFilter, onToggle: toggleFilter, onCheckChange: updateCheckFilter, onClear: clearColumnFilter, onRangeChange: updateRangeFilter, onSort: toggleSort };
  const incrementalRows = useIncrementalRows(orderedFiltered);

  return (
    <section className="window_page bookings_page management_page management_shared_page hotel_services_page">
      <div className="window_banner"><div className="window_banner_copy"><h1>SERVIZI</h1><p>Gestione servizi struttura, tariffe, tipo addebito e quantità default.</p></div></div>
      <div className="management_list_panel">
        <div className="booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar hotel_services_toolbar">
          <OptionsMenu open={optionsOpen} menuRef={optionsMenuRef} disabled={selectedIds.length === 0} confirming={confirmBulkDelete} canEdit={canEditService} canDelete={canDeleteService} blockedClass={blockedActionClass} onBlocked={blockAction} onToggle={() => setOptionsOpen((current) => !current)} onNew={() => openService()} onReset={resetSearchAndFilters} onImport={() => openImportExport("import")} onExport={() => openImportExport("export")} onStartConfirm={() => setConfirmBulkDelete(true)} onCancelConfirm={() => setConfirmBulkDelete(false)} onConfirm={removeSelected} />
          <input className="management_search" value={search} onChange={(e) => setSearch(formatManagementSearchInput(e.target.value))} placeholder="Cerca servizi (es. Colazione, Parcheggio)" />
          <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {filtered.length}</strong></div>
        </div>
        <div className="management_board management_table_board">
          <div className="management_table_shell" role="table" aria-label="Elenco servizi" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
            <div className="management_table_header management_shared_table_header hotel_services_table_header" role="row" ref={filterPanelRef}>
              <div className="management_table_select_head" role="columnheader"><input type="checkbox" checked={allFilteredSelected} ref={(input) => { if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected; }} onChange={() => toggleAllFiltered(!someFilteredSelected)} aria-label="Seleziona tutti i servizi filtrati" /></div>
              <ManagementFilterHeader columnKey="description" label="Descrizione" filterable={false} value={[]} sortDirection={sortState.key === "description" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="rate" label="Prezzo" value={columnFilters.rate} filterType="number" sortDirection={sortState.key === "rate" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="defaultQuantity" label="Qta" value={columnFilters.defaultQuantity} filterType="number" sortDirection={sortState.key === "defaultQuantity" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="taxableTotal" label="Totale Imponibile" value={columnFilters.taxableTotal} filterType="number" sortDirection={sortState.key === "taxableTotal" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="vatRate" label="% IVA" value={columnFilters.vatRate} filterType="number" sortDirection={sortState.key === "vatRate" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="taxAmount" label="Totale Imposta" value={columnFilters.taxAmount} filterType="number" sortDirection={sortState.key === "taxAmount" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="grossTotal" label="Totale Ivato" value={columnFilters.grossTotal} filterType="number" sortDirection={sortState.key === "grossTotal" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="defaultChargeType" label="Addebito" options={filterOptions.defaultChargeType} value={columnFilters.defaultChargeType} sortDirection={sortState.key === "defaultChargeType" ? sortState.direction : ""} {...filterProps} />
              <div className="management_table_actions_head" role="columnheader">Azioni</div>
            </div>
            {loading && <ManagementListLoading label="Caricamento Servizi" />}
            {!loading && error && <div className="management_table_visible_status hotel_services_status is_error">{error}</div>}
            {!loading && !error && filtered.length === 0 && <div className="management_table_visible_status hotel_services_status">Nessun Risultato Trovato</div>}
            <div className="management_table_body" role="rowgroup">
              {incrementalRows.rows.map((item) => {
                const isConfirming = String(confirmDeleteId) === String(item._id);
                return (
                  <article key={item._id} className={`management_table_row hotel_services_table_row${item.active === false ? " is_inactive" : ""}${selectedIds.some((id) => String(id) === String(item._id)) ? " is_selected" : ""}`} data-vesta-focus-id={item._id} role="row">
                    <div className="management_table_cell management_table_select_cell" role="cell"><input type="checkbox" checked={selectedIds.some((id) => String(id) === String(item._id))} onChange={(event) => toggleSelected(item._id, event.target.checked)} aria-label={`Seleziona servizio ${item.description}`} /></div>
                    <div className="management_table_cell management_table_title_cell" role="cell"><strong>{displayValue(item.description)}</strong></div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{formatMoney(item.rate)}</div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{displayValue(item.defaultQuantity)}</div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{formatMoney(serviceAmounts(item).taxableTotal)}</div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{formatPercent(serviceAmounts(item).vatRate)}</div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{formatMoney(serviceAmounts(item).taxAmount)}</div>
                    <div className="management_table_cell management_numeric_cell" role="cell">{formatMoney(serviceAmounts(item).grossTotal)}</div>
                    <div className="management_table_cell" role="cell">{chargeTypeLabel(item.defaultChargeType)}</div>
                    <div className="management_table_actions management_status_actions" role="cell">
                      <button type="button" className={`management_action_button ${item.active === false ? "management_action_success" : "management_action_danger"}${blockedActionClass(canEditService)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => toggleActive(item)}>{item.active === false ? "Attiva" : "Disattiva"}</button>
                      <OrderActionField value={item.order} blocked={!canEditService} onBlocked={blockAction} onMoveUp={() => moveRowOrder(item, -1, orderedFiltered)} onMoveDown={() => moveRowOrder(item, 1, orderedFiltered)} onCommit={(value) => commitRowOrder(item, value)} />
                      {isConfirming ? <><button type="button" className="management_action_button management_action_cancel" onClick={() => setConfirmDeleteId("")}>Annulla</button><button type="button" className={`management_action_button management_action_confirm${blockedActionClass(canDeleteService)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => removeService(item)}>Conferma</button></> : <><button type="button" className={`management_action_button management_action_primary${blockedActionClass(canEditService)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openService(item)}>Modifica</button><button type="button" className={`management_action_button management_action_danger${blockedActionClass(canDeleteService)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => canDeleteService ? setConfirmDeleteId(item._id) : blockAction()}>Elimina</button></>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {showOverlay && <ServiceOverlay draft={draft} sites={sites} error={overlayError} onChange={updateDraft} onClose={() => setShowOverlay(false)} onSave={saveService} />}
      {importExportMode && <ImportExportOverlay mode={importExportMode} title="Servizi" entityLabel="Servizi" fileBaseName="vesta_servizi" fields={SERVICE_IMPORT_EXPORT_FIELDS} rows={orderedFiltered} onClose={() => setImportExportMode("")} onImport={importServices} />}
    </section>
  );
}
